import React, { useState, useRef } from 'react';
import { Modal } from '../ui/Modal';
import { useUIStore } from '../../stores/uiStore';
import { useProposalStore } from '../../stores/proposalStore';
import { Plus, Upload, Link as LinkIcon, Loader2, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';

interface FormData {
  title: string;
  description: string;
  deadline: string;
  budget: string;
  team: string[];
  files: File[];
  links: Array<{ url: string; description: string }>;
  requirements: string;
  naicsCode: string;
  setAside: string;
  agency: string;
}

const initialFormData: FormData = {
  title: '',
  description: '',
  deadline: '',
  budget: '',
  team: [],
  files: [],
  links: [],
  requirements: '',
  naicsCode: '',
  setAside: '',
  agency: '',
};

export function NewProposalModal() {
  const { modals, closeModal } = useUIStore();
  const { createProposal } = useProposalStore();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [saving, setSaving] = useState(false);
  const [newLink, setNewLink] = useState({ url: '', description: '' });

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      setFormData(prev => ({
        ...prev,
        files: [...prev.files, ...acceptedFiles]
      }));
    },
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Process files and create URLs
      const fileUrls = await Promise.all(formData.files.map(async (file) => {
        // In a real app, you would upload files to storage here
        return URL.createObjectURL(file);
      }));

      const proposal = {
        title: formData.title,
        description: formData.description,
        deadline: formData.deadline,
        budget: parseFloat(formData.budget),
        team: formData.team,
        attachments: fileUrls,
        links: formData.links,
        requirements: formData.requirements,
        naicsCode: formData.naicsCode,
        setAside: formData.setAside,
        agency: formData.agency,
        status: 'draft',
      };

      await createProposal(proposal);
      closeModal('newProposal');
      setFormData(initialFormData);
      toast.success('Proposal created successfully');
    } catch (error) {
      console.error('Failed to create proposal:', error);
      toast.error('Failed to create proposal');
    } finally {
      setSaving(false);
    }
  };

  const addLink = () => {
    if (!newLink.url || !newLink.description) {
      toast.error('Please provide both URL and description');
      return;
    }

    setFormData(prev => ({
      ...prev,
      links: [...prev.links, newLink]
    }));
    setNewLink({ url: '', description: '' });
  };

  const removeLink = (index: number) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  const removeFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  return (
    <Modal
      isOpen={modals.newProposal}
      onClose={() => closeModal('newProposal')}
      title="Create New Proposal"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Proposal Title
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              required
            />
          </div>
        </div>

        {/* Contract Details */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="agency" className="block text-sm font-medium text-gray-700">
              Agency
            </label>
            <input
              type="text"
              id="agency"
              value={formData.agency}
              onChange={(e) => setFormData({ ...formData, agency: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="naicsCode" className="block text-sm font-medium text-gray-700">
              NAICS Code
            </label>
            <input
              type="text"
              id="naicsCode"
              value={formData.naicsCode}
              onChange={(e) => setFormData({ ...formData, naicsCode: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="setAside" className="block text-sm font-medium text-gray-700">
              Set-Aside
            </label>
            <select
              id="setAside"
              value={formData.setAside}
              onChange={(e) => setFormData({ ...formData, setAside: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
            >
              <option value="">None</option>
              <option value="8(a)">8(a)</option>
              <option value="HUBZone">HUBZone</option>
              <option value="SDVOSB">SDVOSB</option>
              <option value="WOSB">WOSB</option>
            </select>
          </div>

          <div>
            <label htmlFor="budget" className="block text-sm font-medium text-gray-700">
              Budget
            </label>
            <input
              type="number"
              id="budget"
              value={formData.budget}
              onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label htmlFor="deadline" className="block text-sm font-medium text-gray-700">
              Deadline
            </label>
            <input
              type="date"
              id="deadline"
              value={formData.deadline}
              onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label htmlFor="team" className="block text-sm font-medium text-gray-700">
              Team Members
            </label>
            <select
              id="team"
              multiple
              value={formData.team}
              onChange={(e) => {
                const values = Array.from(e.target.selectedOptions, option => option.value);
                setFormData({ ...formData, team: values });
              }}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
            >
              <option value="john.doe">John Doe</option>
              <option value="jane.smith">Jane Smith</option>
              <option value="bob.wilson">Bob Wilson</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Hold Ctrl/Cmd to select multiple team members
            </p>
          </div>
        </div>

        {/* Requirements */}
        <div>
          <label htmlFor="requirements" className="block text-sm font-medium text-gray-700">
            Requirements
          </label>
          <textarea
            id="requirements"
            value={formData.requirements}
            onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
            rows={4}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
            placeholder="Enter each requirement on a new line"
          />
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Attachments
          </label>
          <div
            {...getRootProps()}
            className="mt-1 flex justify-center rounded-md border-2 border-dashed border-gray-300 px-6 py-4"
          >
            <div className="text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-2">
                <input {...getInputProps()} />
                <p className="text-sm text-gray-600">
                  Drag & drop files here, or click to select files
                </p>
                <p className="text-xs text-gray-500">
                  PDF, DOC, DOCX up to 10MB
                </p>
              </div>
            </div>
          </div>

          {formData.files.length > 0 && (
            <ul className="mt-4 space-y-2">
              {formData.files.map((file, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 p-2"
                >
                  <span className="text-sm text-gray-600">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Related Links */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Related Links
          </label>
          <div className="mt-2 space-y-3">
            <div className="flex space-x-2">
              <input
                type="url"
                value={newLink.url}
                onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                placeholder="Enter URL"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
              <input
                type="text"
                value={newLink.description}
                onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
                placeholder="Link description"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
              <button
                type="button"
                onClick={addLink}
                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>

            {formData.links.map((link, index) => (
              <div key={index} className="flex items-center space-x-2">
                <LinkIcon className="h-4 w-4 text-gray-400" />
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-indigo-600 hover:text-indigo-500"
                >
                  {link.description}
                </a>
                <button
                  type="button"
                  onClick={() => removeLink(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => closeModal('newProposal')}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4" />
                Create Proposal
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}